import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { fromSuggestion } from '../../ParseCSVModal.js'
import { CheckIcon } from '@heroicons/react/24/outline/index.js'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid/index.js'
import { MapEnumValuesModal } from './MapEnumValuesModal.js'
import type { PureMetadata } from '../../../../../../Model/Sample/ISampleMetaData.js'
import type { CSVColumnSelectorReturn, CSVTransform } from '../CSVColumnSelectorReturn.js'
import type { CommonConfig, CSVColumnSelectorValidationState } from '../CSVCommonState.js'
import { CSVMetadataSharedSelect } from '../CSVMetadataSharedSelect.js'
import { isClearToken, readCell } from '../CSVClearSentinel.js'
import { fieldsClearedBy } from '../../../../../../Model/Sample/metadataClearing.js'

export type EnumDTOBaseType = { key: string; label: string }

export type MetadataEntryConfig<TEnumKeyType extends string, TEnumDTOType extends EnumDTOBaseType> = CommonConfig & {
  enumType: Record<TEnumKeyType, TEnumDTOType>
  setter: (p: PureMetadata, value: TEnumKeyType) => void
  setterCustom: (p: PureMetadata, value: string) => void
}

/**
 * Component for selecting enum valued things from a csv
 */
type CSVEnumSelectorProps<TEnumKeyType extends string, TEnumDTOType extends EnumDTOBaseType> = {
  config: MetadataEntryConfig<TEnumKeyType, TEnumDTOType>
  onChange: (config: MetadataEntryConfig<TEnumKeyType, TEnumDTOType>, e: CSVColumnSelectorReturn) => void
  disabled?: boolean
  rawCSVData: Record<string, string>[]
  csvHeaders: string[]
  renderInstead: (instead: ReactNode | undefined) => void
}

export type ValueMapping<TEnumKeyType, TEnumDTOType> =
  | { type: 'DISCARD' }
  | { type: 'AS_CUSTOM' }
  | { type: 'MAP_TO'; value: TEnumKeyType; dto: TEnumDTOType }

export function CSVEnumSelector<TEnumKeyType extends string, TEnumDTOType extends EnumDTOBaseType>(
  props: CSVEnumSelectorProps<TEnumKeyType, TEnumDTOType>
) {
  const [invalidValues, setInvalidValues] = useState<Set<string> | undefined>(undefined)
  const [validationState, setValidationState] = useState<CSVColumnSelectorValidationState>('NO_DATA')
  const [mapping, setMapping] = useState<Record<string, ValueMapping<TEnumKeyType, TEnumDTOType>>>({})
  const [selectedOption, setSelectedOption] = useState<string>('')

  const allOptions = new Set(Object.keys(props.config.enumType))

  const isEnumKey = (value: string): value is TEnumKeyType => {
    return Object.prototype.hasOwnProperty.call(props.config.enumType, value)
  }

  useEffect(() => {
    if (props.rawCSVData && props.config.type) {
      const suggestion = fromSuggestion(props.config.type, props.rawCSVData, props.csvHeaders)
      setSelectedOption(suggestion)

      if (suggestion) {
        const newInvalidValues = findInvalidValues(suggestion, mapping, allOptions)
        setInvalidValues(newInvalidValues)

        const newValidationState = newInvalidValues.size === 0 ? 'VALIDATED' : 'INVALID_VALUES'
        setValidationState(newValidationState)
      }
    }
  }, [props.csvHeaders, props.rawCSVData, props.config.type])

  const reactSelectOptions = [
    {
      value: '',
      label: 'Select Column matching the criteria',
      rawKey: ''
    },
    ...props.csvHeaders.map((d: string) => ({
      value: d,
      label: d,
      rawKey: d
    }))
  ]

  const selectedValue = reactSelectOptions.find((opt) => opt.value === selectedOption) || null

  const findInvalidValues = (
    option: string,
    mapping: Record<string, ValueMapping<TEnumKeyType, TEnumDTOType>>,
    allOptions: Set<string>
  ) => {
    const unmatchedEntries = new Set<string>()
    props.rawCSVData.forEach((row) => {
      const currentValue = row[option]
      if (currentValue && !isClearToken(currentValue) && !mapping[currentValue] && !allOptions.has(currentValue)) {
        unmatchedEntries.add(currentValue)
      }
    })
    return unmatchedEntries
  }

  const handleTransform: CSVTransform = (args) => {
    const cell = readCell(args.row, selectedOption)
    if (cell.kind === 'clear') {
      args.clear(fieldsClearedBy(props.config.type))
      return
    }
    const value = cell.kind === 'value' ? cell.value : undefined
    if (value) {
      if (mapping[value]) {
        switch (mapping[value].type) {
          case 'MAP_TO':
            props.config.setter(args.result, mapping[value].value)
            break
          case 'DISCARD':
            /* noop, skip */ break
          case 'AS_CUSTOM':
            props.config.setterCustom(args.result, value)
            break
        }
      } else {
        /* no mapping present, just try to apply as-is */
        if (isEnumKey(value)) {
          props.config.setter(args.result, value)
        } else {
          console.error('Unexpected case; at this point, ', value, 'should have been a valid enum key in ', props.config.enumType)
        }
      }
    }
  }

  const handleChange = (selected: (typeof reactSelectOptions)[number] | null) => {
    setSelectedOption(selected?.value ?? '')
  }

  const handleMappingChange = (changes: Record<string, ValueMapping<TEnumKeyType, TEnumDTOType>>) => {
    setMapping((prev) => {
      return { ...prev, ...changes }
    })
  }

  useEffect(() => {
    setMapping({})
  }, [selectedOption])

  useEffect(() => {
    if (selectedOption) {
      const newInvalidValues = findInvalidValues(selectedOption, mapping, allOptions)
      setInvalidValues(newInvalidValues)

      const newValidationState = newInvalidValues.size === 0 ? 'VALIDATED' : 'INVALID_VALUES'
      setValidationState(newValidationState)

      props.onChange(props.config, {
        selectedColumn: selectedOption,
        validationState: newValidationState,
        transform: handleTransform
      })
    } else {
      setInvalidValues(undefined)
      setValidationState('NO_DATA')
      props.onChange(props.config, {
        selectedColumn: undefined,
        validationState: 'NO_DATA',
        transform: handleTransform
      })
    }
  }, [mapping])

  return (
    <>
      <CSVMetadataSharedSelect
        config={props.config}
        disabled={props.disabled}
        selectedValue={selectedValue}
        options={reactSelectOptions}
        onChange={handleChange}
        validationState={validationState}
      />
      {/* Status Icon Button - only shown if a column is selected */}
      {validationState !== 'NO_DATA' && (
        <button
          className={`${
            validationState === 'VALIDATED' ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'
          } text-lg`}
          onClick={() => {
            // Open the modal if there are invalid values or if there are any mappings to display
            if ((validationState === 'INVALID_VALUES' && invalidValues && invalidValues.size > 0) || Object.keys(mapping).length > 0) {
              props.renderInstead(
                <MapEnumValuesModal
                  invalidValues={invalidValues ?? new Set()}
                  mapping={mapping}
                  handleMappingChange={handleMappingChange}
                  enumType={props.config.enumType}
                  close={() => props.renderInstead(undefined)}
                />
              )
            }
          }}
          title={
            validationState === 'VALIDATED'
              ? 'All values are valid - click to view or edit mappings'
              : `${invalidValues?.size ?? -1} entries cannot be resolved to allowed values. Click to fix`
          }
        >
          {validationState === 'VALIDATED' ? <CheckIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
        </button>
      )}
      {validationState === 'NO_DATA' && <span></span>}
    </>
  )
}
